import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import Images from "../images";
import axiosInstance from "../Help/AxiosLogicInstance";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import LazyImg from "./LazyImg";

const initialFormData = {
  name: "",
  institute: "",
  email: "",
  contact: "",
  password: "",
  confirmPassword: "",
  year: "",
  state: "",
  city: "",
  district: "",
  pincode: "",
  address: "",
  dob: "",
  doj: "",
  qualification: "",
  reference: "",
  yearOther: "",
  yearQualification: "",
  terms: false,
};

const Signup = ({ show, onClose, openSignIn, onSignupSuccess }) => {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(true);

  const [otherValue, setOtherValue] = useState("");
  // const [showSignIn, setShowSignIn] = useState(false);
  //const [showSignUp, setShowSignUp] = useState(false);

  const [formData, setFormData] = useState(initialFormData);

  const navigate = useNavigate();
  const firstInputRef = useRef(null);
  const emailRef = useRef(null);
  const nameRef = useRef(null);
  const instituteRef = useRef(null);
  const contactRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const stateRef = useRef(null);
  const cityRef = useRef(null);
  const districtRef = useRef(null);
  const pincodeRef = useRef(null);
  const addressRef = useRef(null);

  const focusField = (fieldName) => {
    const refs = {
      email: emailRef,
      name: nameRef,
      institute: instituteRef,
      contact: contactRef,
      password: passwordRef,
      confirmPassword: confirmPasswordRef,
      state: stateRef,
      city: cityRef,
      district: districtRef,
      pincode: pincodeRef,
      address: addressRef
    };

    const ref = refs[fieldName];
    if (ref?.current) {
      ref.current.focus();
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    if (firstInputRef.current) firstInputRef.current.focus();
  }, [step, show]);
  // Clear entered details when the modal closes so the next user starts empty
  useEffect(() => {
    if (!show) {
      setFormData(initialFormData);
      setErrors({});
      setStep(1);
    }
  }, [show]);
  useEffect(() => {
    const handleResize = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    if (errors[name]) setErrors({ ...errors, [name]: undefined });
  };

  // States/Districts data (populate districts when a state is selected)
  const [statesData, setStatesData] = useState([]);
  const [indianStates, setIndianStates] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);

  useEffect(() => {
    const loadStatesCsv = async () => {
      try {
        const resp = await fetch("/Districts/states_districts.csv");
        if (!resp.ok) return;
        const text = await resp.text();
        const lines = text.split('\n').slice(1); // skip header like ScholarshipForm
        const data = lines
          .map(line => {
            const parts = line.split(',').map(s => s.replace(/"/g, '').trim());
            const state = parts[0];
            const district = parts[1];
            return { state, district };
          })
          .filter(d => d.state && d.district);
        setStatesData(data);
        const uniqueStates = Array.from(new Set(data.map(d => d.state))).sort();
        setIndianStates(uniqueStates);
      } catch (err) {
        console.error("Failed to load states/districts CSV", err);
      }
    };

    loadStatesCsv();
  }, []);

  useEffect(() => {
    if (!formData.state) {
      setDistrictOptions([]);
      return;
    }
    const districts = statesData
      .filter(d => d.state === formData.state)
      .map(d => d.district)
      .filter(Boolean)
      .sort();
    setDistrictOptions(districts);
  }, [formData.state, statesData]);

  const validateStep = () => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = "Required";
      if (!formData.institute.trim()) newErrors.institute = "Required";
      if (!formData.email.trim()) newErrors.email = "Required";
      if (!/\S+@\S+\.\S+/.test(formData.email))
        newErrors.email = "Invalid email";
      if (!formData.contact) {
        newErrors.contact = "Required";
      } else if (!/^[6-9]\d{9}$/.test(formData.contact)) {
        newErrors.contact = "Please check your number";
      }
      
      // Password validation
      if (!formData.password) {
        newErrors.password = "Required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (!/(?=.*[a-z])/.test(formData.password)) {
        newErrors.password = "Password must include a lowercase letter";
      } else if (!/(?=.*[A-Z])/.test(formData.password)) {
        newErrors.password = "Password must include an uppercase letter";
      } else if (!/(?=.*\d)/.test(formData.password)) {
        newErrors.password = "Password must include a number";
      } else if (!/(?=.*[@$!%*?&])/.test(formData.password)) {
        newErrors.password = "Password must include a special character (@$!%*?&)";
      }

      // Confirm password validation
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Required";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
      if (!formData.terms) newErrors.terms = "Required";
    } else if (step === 2) {
      if (!formData.year) newErrors.year = "Required";
      if (!formData.state) newErrors.state = "Required";
      if (!formData.city.trim()) newErrors.city = "Required";
      if (!formData.district.trim()) newErrors.district = "Required";
      if (!formData.pincode.trim()) newErrors.pincode = "Required";
      if (!/^\d{6}$/.test(formData.pincode)) newErrors.pincode = "Invalid";
      if (!formData.address.trim()) newErrors.address = "Required";
      
      // Validate DOB
      if (!formData.dob) {
        newErrors.dob = "Required";
      } else {
        const dobDate = new Date(formData.dob);
        const today = new Date();
        if (dobDate > today) {
          newErrors.dob = "Date of birth cannot be in the future";
        }
      }
      
      if (!formData.doj) newErrors.doj = "Required";
      if (!formData.qualification) newErrors.qualification = "Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleNext = async () => {
  try {
    // First do client-side validation
    if (!validateStep()) return;

    // Then do server-side validation for step 1
    if (step === 1) {
      const validationResponse = await axiosInstance.post("/api/Student/ValidateStudentSignupStep1", {
        UserName: formData.name,
        Institution: formData.institute,
        EmailID: formData.email,
        MobileNo: formData.contact,
        Pass: formData.password,
        ConfirmPassword: formData.confirmPassword,
        Terms: formData.terms
      });

      if (validationResponse.data.Status === "200") {
        setStep(step + 1);
      }
    } else {
      setStep(step + 1);
    }
  } catch (err) {
    console.error("Validation error:", err);

    // Handle validation errors from server
    const serverErrors = err.response?.data?.Errors || err.response?.data?.errors;
    if (serverErrors) {
      setErrors(serverErrors);

      // Focus the first field with an error
      const firstErrorField = Object.keys(serverErrors)[0];
      if (firstErrorField) {
        focusField(firstErrorField);
      }

      // Prefer showing email error specifically, else show the first error message
      if (serverErrors.email) {
        toast.error(serverErrors.email);
      } else if (firstErrorField) {
        const msg = serverErrors[firstErrorField];
        if (msg) toast.error(msg);
      } else {
        toast.error(err.response?.data?.Message || "Validation failed");
      }
    } else {
      toast.error(err.response?.data?.Message || "Validation failed");
    }
  }
};
  const handlePrev = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    try {
      await axiosInstance.post("/api/Student/StudentSignUp1", {
        UserName: formData.name,
        Institution: formData.institute,
        OtherIns: formData.otherInstitute || "",
        Qualification: formData.qualification,
        OtherQuly: formData.qualificationOther || "",
        Mopnumber: formData.mopnumber || "",
        Pass: formData.password,
        InstitutionYear: formData.yearOther || formData.year,
        UserState: formData.state,
        UserCity: formData.city,
        DateOfJoiningTheInstitute: formData.doj,
        UserPincode: formData.pincode,
        Address: formData.address,
        EmailID: formData.email,
        MobileNo: formData.contact,
        EnquiryDate: new Date().toISOString(),
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
        TwoStepsDone: 1, // or 0 if not done
        UniqueIdNumber: formData.uniqueIdNumber || "",
        Reference: formData.reference || "",
        OtherQualification: formData.qualificationOther || "",
        District: formData.district,
        UserDob: formData.dob,
      });
      toast.success("Signup successful!");
      onClose();
      if (typeof openSignIn === "function") openSignIn();
    } catch (err) {
      if (err.response?.data?.Status === "409") {
        const errorMessage = err.response.data.Message || "Student already exists";
        toast.error(errorMessage);
        
        // Set the error message for the email field and focus it
        setErrors(prev => ({
          ...prev,
          email: errorMessage
        }));
        setStep(1); // Go back to step 1 if we're on step 2
        setTimeout(() => focusField("email"), 100);
      } else {
        toast.error("Signup failed!");
      }
      console.error(err);
    }
  };
  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      dialogClassName="custom-modal-dialog"
      centered={false} // Remove centering for better height control
      scrollable={false} // Disable modal scrolling
      className="signup-modal"
      backdrop="static" // Prevent closing on backdrop click
      keyboard={false} // Prevent ESC key closing (optional)
    >
      <Modal.Body
        className="p-0"
        style={{ height: "100vh", overflow: "hidden" }}
      >
        <div className="signup-container">
          {/* Left Image */}
          <div className="signup-left d-none d-lg-block position-relative">
            {/* Background image */}
            <LazyImg
              src={Images.Signup}
              alt="Students celebrating academic achievement — Signup background"
              className="signup-bg w-100"
              loading="lazy"
              width={600}
              height={900}
            />

            <h1 className="signup-heading">
              {step === 1
                ? "Welcome! Looks Like You're New Here"
                : "Welcome! Looks Like You're New Here"}
            </h1>
            {/* Titles below */}
            <div className="stepper-titles text-center mb-1 mt-5 pt-5">
              <div className="title-main signup-heading">
                Sign Up To Your Account
              </div>
            </div>
            {/* Stepper overlay */}
            <div className="step-wrapper signup-heading position-absolute top-0 start-50 translate-middle-x mt-5">
              {[1, 2].map((s) => {
                if (step === 1 && s === 2) return null;

                const isDone = step > s;
                const isCurrent = step === s;

                return (
                  <div className="step-item d-inline-block mx-2" key={s}>
                    <div
                      className={`step-circle ${isDone ? "done" : ""} ${
                        isCurrent ? "current" : ""
                      }`}
                    >
                      {isDone ? "✓" : s}
                    </div>
                    <div className="step-name">Step {s}</div>
                  </div>
                );
              })}
              {/*      <img
                src={Images.Arrow} // replace with your arrow asset
                alt="arrow"
                className="step-arrow-img ms-3"
              /> */}
            </div>
          </div>

          {/* Right Form */}
          <div className="signup-right">
            {/* Close Button */}
            <button onClick={onClose} className="close-btn">
              &times;
            </button>

            {/* Stepper Titles */}

            {/* Step Circles with Connectors */}

            {/* Scrollable Form */}
            <div className="signup-form">
              {step === 1 && (
                <div className="d-block d-md-none text-center">
                  <div className="signup-mobile-heading">
                    Sign Up To Your Account
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Step 1 */}
                {step === 1 && (
                  <div className="row custom-row">
                    <div className="col-md-12">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Name <span className="text-danger">*</span>
                        </Form.Label>

                        <Form.Control
                          ref={nameRef}
                          type="text"
                          name="name"
                          autoComplete="off"
                          className="input-box"
                          value={formData.name}
                          onChange={handleChange}
                          isInvalid={!!errors.name}
                        />
                        {/* <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback> */}
                      </Form.Group>
                      {errors.name && (
                        <div className="text-danger small mt-1">
                          {errors.name}
                        </div>
                      )}
                    </div>
                    <div className="col-md-12">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Institute <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          ref={instituteRef}
                          type="text"
                          className="input-box"
                          name="institute"
                          value={formData.institute}
                          onChange={handleChange}
                          isInvalid={!!errors.institute}
                        />
                        {/* <Form.Control.Feedback type="invalid">
                          {errors.institute}
                        </Form.Control.Feedback> */}
                      </Form.Group>
                      {errors.institute && (
                        <div className="text-danger small mt-1">
                          {errors.institute}
                        </div>
                      )}
                    </div>
                    <div className="col-md-12">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Email <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          ref={emailRef}
                          type="email"
                          name="email"
                          autoComplete="off"
                          className="input-box"
                          value={formData.email}
                          onChange={handleChange}
                          isInvalid={!!errors.email}
                        />
                      </Form.Group>
                      {errors.email && (
                        <div className="text-danger small mt-1">
                          {errors.email}
                        </div>
                      )}
                    </div>
                    <div className="col-md-12">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Contact <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          ref={contactRef}
                          type="text"
                          name="contact"
                          autoComplete="off"
                          className="input-box"
                          value={formData.contact}
                          onChange={handleChange}
                          isInvalid={!!errors.contact}
                        />
                        {/* <Form.Control.Feedback type="invalid">
                          {errors.contact}
                        </Form.Control.Feedback> */}
                      </Form.Group>
                      {errors.contact && (
                        <div className="text-danger small mt-1">
                          {errors.contact}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Password <span className="text-danger">*</span>
                        </Form.Label>

                        {/* input + icon only */}
                        <div className="position-relative">
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            autoComplete="new-password"
                            ref={passwordRef}
                            className="input-box pe-5"
                            value={formData.password}
                            onChange={handleChange}
                            isInvalid={!!errors.password}
                            style={{ backgroundImage: "none" }}
                          />

                          {/* Eye icon */}
                          <i
                            className={`fa ${
                              showPassword ? "fa-eye-slash" : "fa-eye"
                            } password-toggle-icon`}
                            onClick={() => setShowPassword(!showPassword)}
                          ></i>
                        </div>

                        {/* Error feedback BELOW the input wrapper */}
                        {errors.password && (
                          <div className="invalid-feedback d-block">
                            {errors.password}
                          </div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Confirm Password{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>

                        {/* Input + icon only */}
                        <div className="position-relative">
                          <Form.Control
                            type={showConfirm ? "text" : "password"}
                            name="confirmPassword"
                            autoComplete="new-password"
                            ref={confirmPasswordRef}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="input-box pe-5"
                            isInvalid={!!errors.confirmPassword}
                            style={{ backgroundImage: "none" }}
                          />

                          {/* Eye icon */}
                          <i
                            className={`fa ${
                              showConfirm ? "fa-eye-slash" : "fa-eye"
                            } password-toggle-icon`}
                            onClick={() => setShowConfirm(!showConfirm)}
                          ></i>
                        </div>

                        {/* Error feedback BELOW the input wrapper */}
                        {errors.confirmPassword && (
                          <div className="invalid-feedback d-block">
                            {errors.confirmPassword}
                          </div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-12">
                      <Form.Group>
                        <Form.Check
                          type="checkbox"
                          name="terms"
                          checked={formData.terms}
                          onChange={handleChange}
                          label={
                            <>
                              I agree to the{" "}
                              <a
                                href="/Final_TermsConditions_MedEngage2.0_8.10.24.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Terms & Conditions
                              </a>{" "}
                              for the program.
                            </>
                          }
                          isInvalid={!!errors.terms}
                        />

                        {/* <Form.Control.Feedback type="invalid">
                          {errors.terms}
                        </Form.Control.Feedback> */}
                      </Form.Group>
                      {errors.terms && (
                        <div className="text-danger small mt-1">
                          {errors.terms}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2 */}
                {step === 2 && (
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Group controlId="yearSelect">
                        <Form.Label className="signup-form-label">
                          Year <span className="text-danger">*</span>
                        </Form.Label>

                        <Form.Select
                          name="year"
                          value={formData.year}
                          onChange={handleChange}
                          isInvalid={!!errors.year}
                          className="input-box"
                        >
                          <option value="">Select Year</option>
                          <option>Grade XI</option>
                          <option>Grade XII</option>
                          <option>First Year</option>
                          <option>Second Year</option>
                          <option>Third Year</option>
                          <option>Fourth Year</option>
                          <option>MBBS Interns</option>
                          <option value="Other">Other</option>
                        </Form.Select>

                        {formData.year === "Other" && (
                          <div className="col-12">
                            <Form.Label className="mt-3 signup-form-label">
                              Please specify
                            </Form.Label>
                            <Form.Control
                              type="text"
                              className="input-box"
                              placeholder="Enter your option"
                              name="yearOther"
                              value={formData.yearOther || ""}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        )}
                      </Form.Group>
                      {errors.year && (
                        <div className="text-danger small mt-1">
                          {errors.year}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          State <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          ref={stateRef}
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          isInvalid={!!errors.state}
                          className="input-box"
                        >
                          <option value="">Select State / UT</option>
                          {indianStates && indianStates.length > 0 ? (
                            indianStates.map((st) => (
                              <option key={st} value={st}>
                                {st}
                              </option>
                            ))
                          ) : (
                            // fallback to a minimal list if CSV not loaded
                            <>
                              <option>Andhra Pradesh</option>
                              <option>Arunachal Pradesh</option>
                              <option>Assam</option>
                              <option>Bihar</option>
                              <option>Chhattisgarh</option>
                              <option>Goa</option>
                              <option>Gujarat</option>
                              <option>Haryana</option>
                              <option>Delhi</option>
                            </>
                          )}
                        </Form.Select>
                      </Form.Group>
                      {errors.state && (
                        <div className="text-danger small mt-1">
                          {errors.state}
                        </div>
                      )}
                    </div>

                    <div className="col-6 col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          City <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          ref={cityRef}
                          type="text"
                          name="city"
                          className="input-box"
                          value={formData.city}
                          onChange={handleChange}
                          isInvalid={!!errors.city}
                        />
                      </Form.Group>
                      {errors.city && (
                        <div className="text-danger small mt-1">
                          {errors.city}
                        </div>
                      )}
                    </div>
                    <div className="col-6 col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          District <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          ref={districtRef}
                          type="text"
                          name="district"
                          value={formData.district}
                          onChange={handleChange}
                          isInvalid={!!errors.district}
                          className="input-box"
                        />
                      </Form.Group>
                      {errors.district && (
                        <div className="text-danger small mt-1">
                          {errors.district}
                        </div>
                      )}
                    </div>

                    <div className="col-6 col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Pincode <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          ref={pincodeRef}
                          type="text"
                          className="input-box"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          isInvalid={!!errors.pincode}
                        />
                      </Form.Group>
                      {errors.pincode && (
                        <div className="text-danger small mt-1">
                          {errors.pincode}
                        </div>
                      )}
                    </div>

                    <div className="col-6 col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          D.O.B <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="dob"
                          className="input-box"
                          value={formData.dob}
                          onChange={handleChange}
                          isInvalid={!!errors.dob}
                          style={{ backgroundImage: "none" }}
                        />
                      </Form.Group>
                      {errors.dob && (
                        <div className="text-danger small mt-1">
                          {errors.dob}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Address <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          ref={addressRef}
                          as="textarea"
                          rows={1}
                          name="address"
                          className="input-box"
                          value={formData.address}
                          onChange={handleChange}
                          isInvalid={!!errors.address}
                        />
                      </Form.Group>
                      {errors.address && (
                        <div className="text-danger small mt-1">
                          {errors.address}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label className="signup-form-label">
                          Date of Joining <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          className="input-box"
                          name="doj"
                          value={formData.doj}
                          onChange={handleChange}
                          isInvalid={!!errors.doj}
                          style={{ backgroundImage: "none" }}
                        />
                      </Form.Group>
                      {errors.doj && (
                        <div className="text-danger small mt-1">
                          {errors.doj}
                        </div>
                      )}
                    </div>

                 
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label className="signup-form-label">
                            Credential & Qualifications{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            name="qualification"
                            className="input-box"
                            value={formData.qualification}
                            onChange={handleChange}
                            isInvalid={!!errors.qualification}
                          >
                            <option value="">Select Qualification</option>
                            <option value="MBBS">MBBS</option>
                            <option value="MD/DNB">MD/DNB</option>
                            <option value="Other">Other</option>
                          </Form.Select>

                          {formData.qualification === "Other" && (
                            <Form.Group className="mt-3">
                              <Form.Label className="signup-form-label">
                                Please specify
                              </Form.Label>
                              <Form.Control
                                type="text"
                                className="input-box"
                                placeholder="Enter your qualification"
                                name="qualificationOther"
                                value={formData.qualificationOther || ""}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          )}
                        </Form.Group>
                        {errors.qualification && (
                          <div className="text-danger small mt-1">
                            {errors.qualification}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label className="signup-form-label">
                            Reference
                          </Form.Label>
                          <Form.Control
                            type="text"
                            className="input-box"
                            name="reference"
                            value={formData.reference}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  
                )}
              </form>
            </div>

            <div className="signup-footer mt-3 d-flex flex-row justify-content-between align-items-center flex-nowrap">
              {/* Left: Previous */}
              {step === 2 && (
                <div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handlePrev}
                  >
                    Previous
                  </button>
                </div>
              )}

              {/* Right: Save / Submit */}
              <div className="d-flex">
                {step === 1 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleNext}
                  >
                    Save & Next
                  </button>
                )}

                {step === 2 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default Signup;
